import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, IconButton, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import EmailOutlinedIcon from "@material-ui/icons/EmailOutlined";
import { TestimonialCard } from "./TestimonialCard";
import { testimonials } from "./testimonialsData";

const REVIEW_MAILTO = `mailto:hi@lucianlazar.io?subject=${encodeURIComponent(
  "lifecoach-review"
)}&body=${encodeURIComponent(
  "Hi Lucian,\n\nName (first name only):\nAge:\nOccupation:\n\nMy review:\n\n\n— Feel free to trim / rewrite before publishing."
)}`;

const useStyles = makeStyles((theme) => ({
  section: {
    padding: theme.spacing(10, 0, 8, 0),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(7, 0, 6, 0),
    },
  },
  header: {
    maxWidth: 1120,
    margin: "0 auto",
    padding: theme.spacing(0, 3, 5, 3),
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0, 2, 3, 2),
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  headerText: {
    display: "flex",
    flexDirection: "column",
  },
  eyebrow: {
    fontFamily: "Roboto Mono, monospace",
    fontSize: "0.72rem",
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.5)",
    marginBottom: theme.spacing(1.5),
  },
  heading: {
    fontFamily: "Roboto, sans-serif",
    fontWeight: 300,
    fontSize: "1.75rem",
    color: "rgba(255,255,255,0.95)",
    lineHeight: 1.2,
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.4rem",
    },
  },
  writeBtn: {
    fontFamily: "Roboto Mono, monospace",
    fontSize: "0.78rem",
    fontWeight: 500,
    letterSpacing: "0.02em",
    color: "rgba(255,255,255,0.85)",
    border: "1px solid rgba(255,255,255,0.18)",
    borderRadius: 999,
    padding: theme.spacing(1, 2.25),
    textTransform: "none",
    background: "transparent",
    transition: "border-color 0.4s ease, color 0.4s ease, background 0.4s ease",
    "&:hover": {
      borderColor: "#00bfbf",
      color: "#fafafa",
      background: "rgba(0,191,191,0.06)",
    },
  },
  writeBtnIcon: {
    fontSize: "1rem",
    marginRight: theme.spacing(1),
  },
  scrollWrap: {
    position: "relative",
  },
  scroller: {
    display: "grid",
    gridAutoFlow: "column",
    gridAutoColumns: "min(340px, 80vw)",
    alignItems: "stretch",
    gap: theme.spacing(6),
    overflowX: "auto",
    padding: theme.spacing(1, 9, 3, 9),
    scrollBehavior: "smooth",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1, 3, 3, 3),
      gap: theme.spacing(4),
      gridAutoColumns: "min(320px, 82vw)",
    },
  },
  fadeLeft: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    width: 56,
    pointerEvents: "none",
    background:
      "linear-gradient(to right, #0a0a0a, rgba(10,10,10,0))",
    zIndex: 1,
    [theme.breakpoints.down("sm")]: {
      width: 20,
    },
  },
  fadeRight: {
    position: "absolute",
    top: 0,
    bottom: 0,
    right: 0,
    width: 56,
    pointerEvents: "none",
    background:
      "linear-gradient(to left, #0a0a0a, rgba(10,10,10,0))",
    zIndex: 1,
    [theme.breakpoints.down("sm")]: {
      width: 20,
    },
  },
  navBtn: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    background: "rgba(255,255,255,0.06)",
    backdropFilter: "blur(6px)",
    color: "rgba(255,255,255,0.85)",
    border: "1px solid rgba(255,255,255,0.12)",
    zIndex: 2,
    boxShadow: "none !important",
    transition: "opacity 0.3s ease, background 0.3s ease",
    "&:hover": {
      background: "rgba(0,191,191,0.15)",
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  navBtnLeft: {
    left: theme.spacing(1),
  },
  navBtnRight: {
    right: theme.spacing(1),
  },
  navBtnHidden: {
    opacity: 0,
    pointerEvents: "none",
  },
}));

export const Testimonials = () => {
  const classes = useStyles();
  const scrollerRef = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const updateEdges = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setAtStart(el.scrollLeft <= 8);
    setAtEnd(el.scrollLeft >= maxScroll - 8);
  }, []);

  useEffect(() => {
    updateEdges();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateEdges, { passive: true });
    window.addEventListener("resize", updateEdges);
    return () => {
      el.removeEventListener("scroll", updateEdges);
      window.removeEventListener("resize", updateEdges);
    };
  }, [updateEdges]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const step = Math.max(el.clientWidth * 0.7, 320);
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <Box className={classes.section}>
      <Box className={classes.header}>
        <Box className={classes.headerText}>
          {/* <Typography className={classes.eyebrow}>What people say</Typography>
          <Typography component="h2" className={classes.heading}>
            People I've helped, in their words.
          </Typography> */}
          <Typography className={classes.eyebrow}>People I've helped, in their words</Typography>
        </Box>
        <Button
          className={classes.writeBtn}
          href={REVIEW_MAILTO}
          disableElevation
        >
          <EmailOutlinedIcon className={classes.writeBtnIcon} />
          Write a review
        </Button>
      </Box>

      <Box className={classes.scrollWrap}>
        <IconButton
          size="small"
          className={`${classes.navBtn} ${classes.navBtnLeft} ${
            atStart ? classes.navBtnHidden : ""
          }`}
          onClick={() => scrollBy(-1)}
          aria-label="Scroll testimonials left"
        >
          <ChevronLeftIcon />
        </IconButton>
        <Box className={classes.fadeLeft} />
        <Box className={classes.scroller} ref={scrollerRef}>
          {testimonials.map((t) => (
            <TestimonialCard {...t} key={t.name} />
          ))}
        </Box>
        <Box className={classes.fadeRight} />
        <IconButton
          size="small"
          className={`${classes.navBtn} ${classes.navBtnRight} ${
            atEnd ? classes.navBtnHidden : ""
          }`}
          onClick={() => scrollBy(1)}
          aria-label="Scroll testimonials right"
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
    </Box>
  );
};
